import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '@/core/permissions/usePermission';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, AuthButton } from '@/components/ui';
import { PlanDesktopTable } from './components/PlanDesktopTable';
import { PlanMobileList } from './components/PlanMobileList';
import { PlanDetailDrawer } from './components/PlanDetailDrawer';
import { useMaintenancePlans, useMaintenancePlanStats } from './hooks/useMaintenancePlan';
import type { MaintenancePlan } from './types/maintenance-plan.types';

export default function MaintenancePlanPage() {
  const { can } = usePermission();
  const { projectId } = useParams<{ projectId: string }>();
  const { data: plans = [], isLoading } = useMaintenancePlans(projectId || '');
  const { data: stats } = useMaintenancePlanStats(projectId || '');
  
  const [selectedPlan, setSelectedPlan] = useState<MaintenancePlan | null>(null);

  if (!projectId) return null;

  return (
    <PageLayout>
      <PageLayout.Header
        title="Kế hoạch bảo dưỡng"
        breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Kế hoạch' }]}
        description="Thiết lập kế hoạch chăm sóc cây xanh, thảm cỏ, cây bụi và khối lượng bảo dưỡng theo hợp đồng."
      >
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-copy-line">Nhân bản</Button>
          <Button variant="outline" icon="ri-download-line">Xuất KH</Button>
        </div>
      </PageLayout.Header>

      {/* Context Bar */}
      <div className="flex flex-wrap gap-4 px-4 py-3 bg-white border-b border-slate-200 text-sm">
        <div><span className="text-os-muted">Dự án:</span> <span className="font-medium text-os-text">Vinhomes Grand Park</span></div>
        <div><span className="text-os-muted">Hợp đồng:</span> <span className="font-medium text-os-text">HD-BD-2026-001</span></div>
        <div><span className="text-os-muted">Giai đoạn:</span> <span className="font-medium text-os-text">04/2026 - 06/2026</span></div>
      </div>

      {/* KPI Board */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 p-4 pb-0">
        <KPICard title="Tổng kế hoạch" value={stats?.total_plans || 0} icon="ri-calendar-todo-line" />
        <KPICard title="Đang hiệu lực" value={stats?.active_plans || 0} icon="ri-checkbox-circle-line" variant="success" />
        <KPICard title="Chờ duyệt" value={stats?.pending_approval || 0} icon="ri-time-line" variant="warning" />
        <KPICard title="Hết hiệu lực" value={stats?.expired_plans || 0} icon="ri-error-warning-line" variant="neutral" />
        <KPICard title="Sắp rà soát" value={stats?.needs_review || 0} icon="ri-search-eye-line" variant="info" />
        <KPICard title="Tổng giờ dự kiến" value={(stats?.total_estimated_hours || 0).toLocaleString()} icon="ri-time-zone-line" />
        <KPICard title="Nhân sự chuẩn" value={stats?.standard_personnel || 0} icon="ri-group-line" />
        <KPICard title="Đã tạo lịch" value={`${stats?.schedule_completion_rate || 0}%`} icon="ri-calendar-check-line" />
      </div>

      <PageLayout.Toolbar
        primaryAction={
          <AuthButton hasPermission={can('maintenance_plans', 'create')} icon="ri-add-line">
            Tạo kế hoạch
          </AuthButton>
        }
      >
        <div className="flex items-center gap-3 w-full md:w-auto">
          <Search placeholder="Tìm mã KH, tên KH..." className="w-full md:w-64" />
          <Filter 
            options={[
              { id: 'zone', label: 'Khu vực', type: 'select', choices: [{value: 'z1', label: 'Zone A'}] },
              { id: 'status', label: 'Trạng thái', type: 'select', choices: [{value: 'active', label: 'Đang hiệu lực'}] }
            ]} 
            onFilterChange={() => {}}
          />
        </div>
      </PageLayout.Toolbar>

      <PageLayout.Content>
        {/* Desktop View */}
        <div className="hidden md:block">
          <PlanDesktopTable 
            data={plans} 
            isLoading={isLoading} 
            onRowClick={setSelectedPlan} 
          />
        </div>
        
        {/* Mobile View */}
        <div className="block md:hidden">
          <PlanMobileList 
            data={plans} 
            isLoading={isLoading} 
            onRowClick={setSelectedPlan} 
          />
        </div>
      </PageLayout.Content>

      <PlanDetailDrawer 
        plan={selectedPlan} 
        isOpen={!!selectedPlan} 
        onClose={() => setSelectedPlan(null)} 
      />
    </PageLayout>
  );
}
